/**
 * `@font-face` rules for uploaded (non-Google) fonts.
 *
 * These used to be injected straight from the panels — TextAndFont, TickerFont and every
 * GraphicTile — as one fresh `<style>` element per font, with no deduplication and no
 * cleanup. TextAndFont remounts on every selection change, so at 20 fonts a hundred clicks
 * left 2 000 stylesheets in `<head>`. That is not only DOM: every new stylesheet invalidates
 * style resolution for the WHOLE document, which on a 40 000-node editor is a visible
 * "Recalculate style" block in the profile.
 *
 * Here every rule is written once, into a single shared `<style>` element.
 *
 * Google fonts have their own path — see `injectGoogleFonts` in googleFonts.helpers.
 */

const STYLE_ELEMENT_ID = "tw-font-faces"

/** `name|url` of every rule already written */
const injected = new Set<string>()

type FontFaceSource = {
    name?: string | null
    url?: string | null
}

const getStyleElement = (): HTMLStyleElement => {
    const existing = document.getElementById(STYLE_ELEMENT_ID)
    if (existing) return existing as HTMLStyleElement

    const styleElement = document.createElement("style")
    styleElement.id = STYLE_ELEMENT_ID
    document.head.appendChild(styleElement)
    return styleElement
}

/**
 * Adds an `@font-face` rule for every font that does not have one yet. Safe to call as often
 * as you like — repeated calls with the same fonts do nothing at all.
 */
export const injectFontFaces = (fonts: FontFaceSource[]): void => {
    const rules: string[] = []

    fonts.forEach((font) => {
        if (!font?.name || !font?.url) return

        const key = `${font.name}|${font.url}`
        if (injected.has(key)) return
        injected.add(key)

        // quoted: family names may contain spaces and urls may contain parentheses
        rules.push(
            `@font-face { font-family: ${JSON.stringify(font.name)}; src: url(${JSON.stringify(
                font.url
            )}); }`
        )
    })

    if (rules.length === 0) return

    getStyleElement().appendChild(document.createTextNode(rules.join("\n")))
}
